import { BEIGE_300, BEIGE_600, BEIGE_800, BLUE_100, BLUE_300, BLUE_700, CORAL_100, CORAL_300, CORAL_700, GRAY_700, GRAY_900, GREEN_100, GREEN_300, YELLOW_100, YELLOW_300, YELLOW_700 } from 'constants/styling/theme'
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material'
import React, { ReactNode, useMemo } from 'react'

import { BKBNThemeWrapper } from '../BKBNThemeWrapper'
import { BorderBoxWrapper } from '../BorderBoxWrapper'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Trans } from 'react-i18next'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'

export type SystemType = 'info' | 'error' | 'warning' | 'success' | 'system'

const BorderColorVariantMap: Record<SystemType, string> = {
  error: CORAL_300,
  success: GREEN_300,
  warning: YELLOW_300,
  info: BEIGE_600,
  system: BLUE_300,
}

const BackgroundUpbrandingColorVariantMap: Record<SystemType, string> = {
  error: CORAL_100,
  success: GREEN_100,
  warning: YELLOW_100,
  info: BEIGE_300,
  system: BLUE_100,
}

const IconColorVariantMap: Record<SystemType, string> = {
  error: CORAL_700,
  success: GRAY_700,
  warning: YELLOW_700,
  info: BEIGE_800,
  system: BLUE_700,
}

export interface SystemMessageProps {
  /** Text content of the message */
  message: string | ReactNode
  /** Title of the box */
  title?: string
  /** Style variant of the box. */
  variant?: SystemType
  /**
   * Render [message] without the `<Trans>` wrapper. Set this whenever the content is NOT a
   * translation key — backend-authored text, or anything a user typed. `<Trans>` serialises its
   * children to a string and re-parses them as markup, so a bare `<` in the content silently
   * truncates everything after it (verified with an initialised i18n instance).
   */
  isRawContent?: boolean
  /** Optional extra styling */
  sx?: SxProps<Theme>
  /** Buttons under the message, e.g. retry (compose `MUIButton type="secondaryBorder" size="sm"`) */
  actions?: ReactNode
  /** Live-region role of the box: `alert` for a problem to notice now, `status` for a quiet update */
  role?: 'alert' | 'status'
}

/**
 * Component for displaying system messages to user.
 * 
 * @example
 * <SystemMessage message="I love snails." variant="info"/> 
 */
export const SystemMessage: React.FC<SystemMessageProps> = ({
  message,
  title,
  variant = 'system',
  isRawContent = false,
  sx = {},
  actions,
  role,
}) => {

  const systemIcon = useMemo(() => {
    switch (variant) {
      case 'error':
        return <ErrorOutlineOutlinedIcon sx={{ fontSize: 18, color: IconColorVariantMap[variant] }} />
      case 'success':
        return <CheckCircleOutlinedIcon sx={{ fontSize: 18, color: IconColorVariantMap[variant] }} />
      case 'warning':
        return <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: IconColorVariantMap[variant] }} />
      case 'info':
      case 'system':
        return <InfoOutlinedIcon sx={{ fontSize: 18, color: IconColorVariantMap[variant] }} />
      default:
        return <></>
    }
  }, [variant])

  return (
    <BKBNThemeWrapper>
      <BorderBoxWrapper
        gap={1}
        padding={1.5}
        display="flex"
        alignItems="center"
        borderColor={BorderColorVariantMap[variant]}
        backgroundColor={BackgroundUpbrandingColorVariantMap[variant]}
        role={role}
        sx={{ alignItems: 'flex-start', ...sx }}
      >

        <Box>
          {systemIcon}
        </Box>

        <Stack gap=".2rem">

          {title &&
            <Typography variant="text-sm" fontWeight="semiBold" color={GRAY_900} mt={-.2}>
              {title}
            </Typography>
          }

          {isRawContent
            ? (
              <Typography variant="text-sm" color={GRAY_900} component="div">
                {message}
              </Typography>
            )
            : (
              <Trans parent={undefined}>
                <Typography variant="text-sm" color={GRAY_900}>
                  {message}
                </Typography>
              </Trans>
            )
          }

          {!!actions &&
            <Stack direction="row" flexWrap="wrap" gap=".8rem" mt=".6rem">
              {actions}
            </Stack>
          }

        </Stack>

      </BorderBoxWrapper>
    </BKBNThemeWrapper>
  )
}
